// Warp presets tuned on shaders.paper.design. The brand's drives the splash and
// the AI button; the alarm's sits behind a glowing error. Colours are tokens.

export const BRAND_WARP = {
  proportion: 0.64,
  softness: 1,
  distortion: 0.2,
  swirl: 0.86,
  swirlIterations: 7,
  shape: "edge",
  shapeScale: 0.6,
  scale: 0.68,
  rotation: 360,
} as const;

/** The preset's speed; reduced motion holds the first frame. Every Warp moves at it. */
export const WARP_SPEED = 0.4;

/**
 * The AI button's Warp: the brand's, calmer (low distortion), seen closer
 * (larger scale) with smaller shapes, so in a small window the colours blend
 * instead of cutting in blocks. Colours: the spark-* tokens.
 */
export const SPARK_WARP = {
  ...BRAND_WARP,
  distortion: 0.11,
  shapeScale: 0.3,
  scale: 0.6,
  speed: WARP_SPEED,
} as const;

/** The alarm's Warp, behind a glowing error banner. Colours: the alarm-* tokens. */
export const ALARM_WARP = {
  proportion: 0.5,
  softness: 1,
  distortion: 0.09,
  swirl: 0.9,
  swirlIterations: 6,
  shape: "checks",
  shapeScale: 0.25,
  scale: 2.5,
  rotation: 1.35,
} as const;

/** Poured paint behind the landing's hero. Colours: the marble-* tokens. */
export const MARBLE_WARP = {
  proportion: 0.5,
  softness: 1,
  distortion: 0.45,
  swirl: 0.4,
  swirlIterations: 14,
  shape: "edge",
  shapeScale: 0.5,
  scale: 1.1,
  rotation: 210,
} as const;

/** The marble as wide soft pools instead of veins. Same colours. */
export const MARBLE_DRIFT_WARP = {
  proportion: 0,
  softness: 1,
  distortion: 0.29,
  swirl: 0,
  swirlIterations: 2,
  shape: "edge",
  shapeScale: 0.5,
  scale: 1.44,
  rotation: 212,
} as const;

/** The drift's own speed: its pools are wide, so it has to move quickly to read as moving. */
export const MARBLE_DRIFT_SPEED = 3;

/**
 * The Warike landing's hero field in the dark theme (D1277): Sergio's values, 31 October 2026.
 * Colours: the field-* tokens, in the order `FIELD_WARP_COLOUR_TOKENS` gives.
 */
export const FIELD_WARP = {
  proportion: 0.5,
  softness: 1.5,
  distortion: 0.4,
  swirl: 0,
  swirlIterations: 8,
  shape: "edge",
  shapeScale: 0,
  scale: 0.62,
  rotation: 53,
  offsetX: -0.26,
  offsetY: 0.48,
} as const;

/** The same field in the light theme (D1277). Sergio's values match the dark ones; its colours do not. */
export const FIELD_WARP_LIGHT = { ...FIELD_WARP } as const;

/** Each theme's order for the three colours: black first at night, blue first by day (D1277). */
export const FIELD_WARP_COLOUR_TOKENS = {
  dark: ["field-base", "field-harmonic", "field-spark"],
  light: ["field-harmonic", "field-spark", "field-base"],
} as const;

/** The field's own speed, same provenance as its shape. Reduced motion holds the first frame. */
export const FIELD_WARP_SPEED = 0.8;

/** How much of the field's height, from the bottom, fades into the band below (D1274). */
export const FIELD_FADE_SHARE = 0.4;

/** How far the fade has covered the field at each step down it. The gradient paints these. */
export const FIELD_FADE_STOPS = [0, 0.03, 0.1, 0.22, 0.35, 0.5, 0.65, 0.78, 0.9, 0.97, 1] as const;
